import React, { useEffect, useMemo, useState } from "react";
import Header from "../../header/header";
import { Button, InputAdornment, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Company } from "../../models";
import { getCompanies, updateCompany } from "../../api";
import { CompanyTableRow } from "../components/CompanyTableRow";
import { CompanyFormDialog } from "../components/CompanyFormDialog";

export const CompanyListPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Data state
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Search state
  const [restore, setRestore] = useState(false);
  const [query, setQuery] = useState("");

  // Dialog state - null when closed, undefined company when creating
  const [dialog, setDialog] = useState<{ company?: Company } | null>(null);

  const loading = useMemo(() => !companies && !error, [companies, error]);

  const searchCompanies = useMemo(() => companies?.filter(company => {
    const queryMatch = company.name.toLowerCase().includes(query.toLowerCase());

    return queryMatch && (restore ? company.status === "deleted" : company.status !== "deleted");
  }), [companies, query, restore]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setError(null);

    try {
      // This page is admin-only, so always list every company
      setCompanies(await getCompanies(true));
    } catch (error) {
      setError(error as string);
    }
  }

  async function handleStatusChange(company: Company, status: Company["status"]) {
    try {
      await updateCompany(company.id, { status });
      enqueueSnackbar(status === "deleted" ? "Company deleted" : "Company restored", { variant: "success" });
      await fetchData();
    } catch (e) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
      console.error(e);
    }
  }

  return (
    <>
      <Header />
      {!loading && !error && <div className="content-header" style={{ top: "0.5rem", width: "90%", left: "6.25rem" }}>
        <div className="left" style={{ display: "flex" }}>
          <TextField
            label="Search"
            style={{ marginRight: "10px" }}
            onChange={(e) => setQuery(e.target.value)}
            focused
            size="small"
            value={query}
            className="foreInputWhite"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className="right" style={{ display: "flex" }}>
          <Button style={{ marginRight: "10px" }} variant="contained" color="primary" className="spacer" onClick={() => setDialog({})}>New</Button>
          {restore
            ? <Button variant="contained" color="primary" onClick={() => setRestore(false)} className="spacer">Normal</Button>
            : <Button variant="contained" color="primary" onClick={() => setRestore(true)} className="spacer">Restore</Button>
          }
          <Link to="/"><Button style={{ marginLeft: "10px" }} variant="contained" color="primary" className="spacer">Back</Button></Link>
        </div>
      </div>}
      {loading && <div style={{ width: "50%" }}>
        <LinearProgress />
      </div>}
      {error && <div>Error: {error}</div>}
      {!loading && !error && <div className="profiles-container" style={{ width: "70%", top: "1rem", maxHeight: "80vh" }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead className='table_head'>
              <TableRow>
                <TableCell className="table_cell top_row" style={{ fontWeight: "600", width: "10%" }}>ID</TableCell>
                <TableCell className="table_cell top_row" style={{ fontWeight: "600", width: "50%" }}>Name</TableCell>
                <TableCell className="table_cell top_row" style={{ fontWeight: "600", width: "15%" }}>Date Created</TableCell>
                <TableCell className="table_cell top_row" style={{ fontWeight: "600" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {searchCompanies?.map((company, index) => (
                <CompanyTableRow
                  key={company.id}
                  company={company}
                  dark={index % 2 === 0}
                  restore={restore}
                  onCompanyEdit={(company) => setDialog({ company })}
                  onCompanyStatusChange={handleStatusChange}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>}
      {dialog && <CompanyFormDialog
        company={dialog.company}
        onClose={() => setDialog(null)}
        onCompanySave={() => {
          setDialog(null);
          fetchData();
        }}
      />}
    </>
  );
};
